/**
 * Shop-by-artist browser.
 *
 * Swaps the product grid in place when an artist is picked, without leaving the page.
 * The grid itself is rendered by Shopify: each artist link is a real collection URL, so
 * `?view=artist-grid` returns that collection's product grid built from
 * templates/collection.artist-grid.json. Nothing about product cards or pagination is
 * re-implemented here.
 *
 * Shopify names that section `template--<template id>__main`, and the template id isn't
 * knowable from Liquid on this page — so the first response is fetched whole, the id is
 * read off it, and every later fetch passes it as `section_id` to get the section alone
 * (~27KB instead of ~200KB). The id is cached for the session and re-learned if it ever
 * stops resolving, which is what happens when the theme is republished.
 */

const VIEW = 'artist-grid';
/** Storefront collections feed page size, and a stop so a broken feed can't loop forever. */
const FEED_PAGE_SIZE = 250;
const FEED_MAX_PAGES = 8;
const SECTION_SELECTOR = '[id^="shopify-section-template--"][id$="__main"]';
const CACHE_KEY = 'artist-browser-section-id';

/**
 * Pulls the rendered section out of a fetched document.
 * @param {string} html
 * @param {string | null} sectionId - Known section id, when there is one.
 * @returns {{ section: Element, doc: Document, id: string } | null}
 */
function parseSection(html, sectionId) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const section =
    (sectionId && doc.getElementById(`shopify-section-${sectionId}`)) || doc.querySelector(SECTION_SELECTOR);

  return section ? { section, doc, id: section.id.replace('shopify-section-', '') } : null;
}

/** @returns {string | null} */
function readCachedSectionId() {
  try {
    return sessionStorage.getItem(CACHE_KEY);
  } catch {
    return null;
  }
}

/** @param {string} id */
function cacheSectionId(id) {
  try {
    sessionStorage.setItem(CACHE_KEY, id);
  } catch {
    // Private mode or blocked storage: the id is still held in memory for this page.
  }
}

/**
 * Runs the component scripts the fetched markup depends on. Scripts inserted as HTML
 * never execute, so custom elements inside the injected grid (quick add, variant
 * swatches, pagination) would stay un-upgraded without this. Modules are evaluated once
 * per URL, so re-adding an already-loaded src is harmless — it is skipped anyway.
 * @param {Document} doc
 */
function loadScripts(doc) {
  const loaded = new Set(Array.from(document.querySelectorAll('script[src]'), (script) => script.src));

  for (const script of doc.querySelectorAll('script[src]')) {
    if (loaded.has(script.src)) continue;
    const clone = document.createElement('script');
    clone.src = script.src;
    if (script.type) clone.type = script.type;
    document.head.append(clone);
  }
}

class ArtistBrowser extends HTMLElement {
  /** Guards against an earlier fetch resolving after a later one. */
  #request = 0;

  /** @type {HTMLAnchorElement | null} */
  #current = null;

  /** @type {string | null} */
  #sectionId = readCachedSectionId();

  connectedCallback() {
    this.list = this.querySelector('[data-artist-list]');
    this.results = this.querySelector('[data-artist-results]');
    this.search = this.querySelector('[data-artist-search]');

    if (!this.list || !this.results) return;

    this.addEventListener('click', this.#handleClick);
    this.search?.addEventListener('input', this.#handleSearch);
    window.addEventListener('popstate', this.#handlePopState);

    this.#restoreFromUrl();
    this.#loadFeed();
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.#handlePopState);
  }

  /**
   * @param {string | null} handle
   * @returns {HTMLAnchorElement | null}
   */
  #linkFor(handle) {
    if (!handle) return null;
    return this.list.querySelector(`[data-artist="${CSS.escape(handle)}"]`);
  }

  /**
   * Tops the panel up past Shopify's 50-collection picker cap. The section only sets
   * data-artist-feed when it fell back to that picker — a curated artist menu is left
   * exactly as the merchant built it.
   */
  async #loadFeed() {
    const feed = this.dataset.artistFeed;
    const template = this.list.firstElementChild;
    if (!feed || !template) return;

    // Read before the first render's history.replaceState rewrites it to the fallback artist.
    const wanted = new URLSearchParams(window.location.search).get('artist');

    const seen = new Set(Array.from(this.list.querySelectorAll('[data-artist]'), (link) => link.dataset.artist));
    let added = false;

    for (let page = 1; page <= FEED_MAX_PAGES; page++) {
      let collections;

      try {
        const response = await fetch(`${feed}?limit=${FEED_PAGE_SIZE}&page=${page}`);
        if (!response.ok) break;
        ({ collections } = await response.json());
      } catch {
        break;
      }

      if (!collections?.length) break;

      for (const { handle, title, products_count: count } of collections) {
        // An empty collection would render an empty grid, so it isn't an artist worth listing.
        if (!handle || seen.has(handle) || count === 0) continue;
        seen.add(handle);
        this.list.append(this.#buildItem(template, handle, title));
        added = true;
      }

      if (collections.length < FEED_PAGE_SIZE) break;
    }

    if (!added) return;

    // The Liquid list is alphabetical; keep the merged one that way too.
    this.list.append(
      ...Array.from(this.list.children).sort((a, b) => a.dataset.name.localeCompare(b.dataset.name))
    );

    if (this.search?.value) this.#handleSearch();
    // A deep link to an artist that only exists in the feed had nothing to match on load.
    const link = this.#linkFor(wanted);
    if (link && link !== this.#current) this.#show(link, null, false);
  }

  /**
   * @param {Element} template - An existing item, cloned to keep the theme's markup and icon.
   * @param {string} handle
   * @param {string} title
   */
  #buildItem(template, handle, title) {
    const item = /** @type {HTMLElement} */ (template.cloneNode(true));
    const link = /** @type {HTMLAnchorElement} */ (item.querySelector('[data-artist]'));

    item.hidden = false;
    item.dataset.name = title.toLowerCase();
    link.href = `/collections/${handle}`;
    link.dataset.artist = handle;
    link.setAttribute('aria-current', 'false');
    item.querySelector('.artist-browser__name').textContent = title;

    return item;
  }

  #restoreFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    // Fall back to the first artist so the page never opens on an empty column.
    const link = this.#linkFor(params.get('artist')) ?? this.list.querySelector('[data-artist]');
    if (link) this.#show(link, params.get('page'), false);
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

    const target = /** @type {Element} */ (event.target);

    const artist = target.closest('[data-artist]');
    if (artist instanceof HTMLAnchorElement) {
      event.preventDefault();
      this.#show(artist, null, true);
      return;
    }

    const page = target.closest('.pagination__link[href]');
    if (page instanceof HTMLAnchorElement && this.results.contains(page) && this.#current) {
      event.preventDefault();
      const pageNumber = new URL(page.href, window.location.origin).searchParams.get('page');
      this.#show(this.#current, pageNumber, true);
      this.results.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  #handleSearch = () => {
    const query = this.search.value.trim().toLowerCase();

    for (const item of this.list.children) {
      item.hidden = query !== '' && !item.dataset.name.includes(query);
    }
  };

  #handlePopState = () => this.#restoreFromUrl();

  /**
   * @param {HTMLAnchorElement} link - The artist link to render.
   * @param {string | null} page - Pagination page number, if any.
   * @param {boolean} push - Whether to add a history entry (false while restoring one).
   */
  async #show(link, page, push) {
    const request = ++this.#request;
    this.#current = link;

    for (const other of this.list.querySelectorAll('[data-artist]')) {
      other.setAttribute('aria-current', String(other === link));
    }

    this.results.setAttribute('aria-busy', 'true');

    const url = new URL(link.href, window.location.origin);
    url.searchParams.set('view', VIEW);
    if (page) url.searchParams.set('page', page);
    if (this.#sectionId) url.searchParams.set('section_id', this.#sectionId);

    let rendered = await this.#fetch(url);

    if (!rendered && this.#sectionId) {
      // The cached id no longer resolves. Drop it, fetch the whole page and re-learn it.
      this.#sectionId = null;
      url.searchParams.delete('section_id');
      rendered = await this.#fetch(url);
    }

    if (request !== this.#request) return;

    this.results.setAttribute('aria-busy', 'false');

    if (!rendered) return;

    if (rendered.id !== this.#sectionId) {
      this.#sectionId = rendered.id;
      cacheSectionId(rendered.id);
    }

    this.results.replaceChildren(...rendered.section.childNodes);
    loadScripts(rendered.doc);
    // ponytail: the injected <results-list> prefetches one page against this page's URL
    // before finding it has no infinite-scroll trigger. One small wasted request per
    // selection; worth removing only if it shows up in the network budget.

    const next = new URL(window.location.href);
    next.searchParams.set('artist', link.dataset.artist);
    if (page) {
      next.searchParams.set('page', page);
    } else {
      next.searchParams.delete('page');
    }
    history[push ? 'pushState' : 'replaceState']({}, '', next);
  }

  /** @param {URL} url */
  async #fetch(url) {
    try {
      const response = await fetch(url);
      if (!response.ok) return null;
      return parseSection(await response.text(), this.#sectionId);
    } catch {
      return null;
    }
  }
}

if (!customElements.get('artist-browser-component')) {
  customElements.define('artist-browser-component', ArtistBrowser);
}
